/**
 * UI-37: Action Execution API Service
 * UI-38A: Updated to match enriched response shape
 *
 * Frontend client for the POST /ops/actions/execute endpoint.
 * Sends action execution requests to the backend and returns real mutation results.
 */

import { API_BASE } from '@/config/api';

// ─── Types (mirrors backend ActionExecutionTypes) ────────────────────────────

export interface ActionExecutionRequest {
  readonly actionId: string;
  readonly ctaType: 'dispatch' | 'assign' | 'escalate' | 'prioritize' | 'recover' | 'navigate';
  readonly domain: 'housekeeping' | 'operations' | 'maintenance' | 'staff';
  readonly payload?: {
    readonly target?: string;
    readonly action?: string;
  };
}

export interface ActionMutationDisplay {
  readonly entityLabel?: string;
  readonly roomNumber?: string;
  readonly staffName?: string;
}

export interface ActionMutation {
  readonly entityType: 'request' | 'task' | 'staff';
  readonly entityId: string;
  readonly action: string;
  readonly previousState?: string;
  readonly newState: string;
  readonly timestamp: number;
  readonly display: ActionMutationDisplay;
}

export interface MetricsSnapshot {
  readonly backlog: number;
  readonly slaBreaches: number;
  readonly utilization: number;
}

export interface ActionExecutionResult {
  readonly success: boolean;
  readonly message: string;
  readonly execution: {
    readonly mutations: ActionMutation[];
    readonly mutatedCount: number;
    readonly executionType: string;
    readonly beforeSnapshot: MetricsSnapshot;
    readonly afterSnapshot: MetricsSnapshot;
  };
}

// ─── API Call ────────────────────────────────────────────────────────────────

const ACTION_TIMEOUT_MS = 15_000;

export async function executeActionOnBackend(
  request: ActionExecutionRequest,
): Promise<ActionExecutionResult> {
  const url = `${API_BASE}/ops/actions/execute`;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ACTION_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(
      (errorBody as { error?: string }).error ?? `Action execution failed: ${response.statusText}`,
    );
  }

  return response.json();
}
